import { type ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import useProject from 'hooks/project-details/useProject';
import get from 'lodash/get';
import LoadingOverlay from 'components/common/LoadingOverlay';
import { BUYING_HOUSE, NEW_APARTMENT, NEW_BUILDING, RENOVATION_HOUSE } from 'constants/constants';
import Alert from 'components/common/Alert';
import { useIntl, type IntlShape } from 'react-intl';
import { grabErrorMessage } from 'utils/helpers';
import type { ProjectQuery } from 'graphql/generated/graphql';
import HouseDetailsCalculator from './BuildHouseCalculator';
import ApartmentDetailsCalculator from './ApartmentDetailsCalculator';
import BuyingHouseDetailsCalculator from './BuyingHouseDetailsCalculator';
import RenovationHouseCalculator from './RenovationHouseCalculator';

interface ICalculatorProps {
  project: ProjectQuery['project'];
}

interface IProjectDetailsCalculatorProps {
  projectId: string;
  t: IntlShape['formatMessage'];
}

const projectTypeToComponentMap: Record<string, ComponentType<ICalculatorProps> | undefined> = {
  [NEW_BUILDING]: HouseDetailsCalculator,
  [NEW_APARTMENT]: ApartmentDetailsCalculator,
  [BUYING_HOUSE]: BuyingHouseDetailsCalculator,
  [RENOVATION_HOUSE]: RenovationHouseCalculator,
};

// TODO(MW-27): Sprengnetter is intentionally deactivated for now. Remove the
// remaining legacy components, hooks and GraphQL code in a separate cleanup
// task after the client confirms that legacy-project compatibility is not needed.

const ProjectDetailsCalculator = ({ projectId, t }: IProjectDetailsCalculatorProps) => {
  const [project, loading, error] = useProject(projectId);
  const type = get(project, 'type', '');

  if (loading) return <LoadingOverlay />;

  const Calculator = projectTypeToComponentMap[type];

  if (error) {
    return (
      <Alert color="danger" withContainer>
        {grabErrorMessage(error)}
      </Alert>
    );
  }

  if (!project) {
    return (
      <Alert color="danger" withContainer>
        {t({ id: 'app.project_not_found' })}
      </Alert>
    );
  }

  if (!Calculator) {
    return (
      <Alert color="danger" withContainer>
        {t({ id: 'app.project_type_not_supported' })}
      </Alert>
    );
  }

  return <Calculator {...{ project }} />;
};

export default () => {
  const { id: projectId } = useParams<{ id?: string }>();
  const { formatMessage: t } = useIntl();

  if (!projectId) {
    return (
      <Alert color="danger" withContainer>
        {t({ id: 'app.project_not_found' })}
      </Alert>
    );
  }

  return <ProjectDetailsCalculator {...{ projectId, t }} />;
};
